import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { entities } from "@/api/apiClient";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Calendar,
  Building2,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, eachMonthOfInterval, addMonths, startOfMonth, endOfMonth } from "date-fns";
import { safeParseISO } from "@/utils";
import { ptBR } from "date-fns/locale";

/** Normaliza valores monetários (API: número, string, formato BR) — alinhado a VisualizarContrato */
function parseMoneyNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    let t = value.trim().replace(/\s/g, "");
    if (!t) return 0;
    const commaAsDecimal = /,\d{1,4}$/.test(t);
    if (commaAsDecimal) {
      t = t.replace(/\./g, "").replace(",", ".");
    } else {
      t = t.replace(/,/g, "");
    }
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === "object" && value !== null) {
    if (typeof value.toNumber === "function") {
      try {
        const n = value.toNumber();
        return Number.isFinite(n) ? n : 0;
      } catch {
        /* continua */
      }
    }
    const n = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function countCalendarMonthsInclusive(inicio, fim) {
  const a = new Date(inicio.getFullYear(), inicio.getMonth(), 1);
  const b = new Date(fim.getFullYear(), fim.getMonth(), 1);
  return Math.max(1, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1);
}

/** YYYY-MM no calendário local (igual ao eixo dos gráficos / date-fns format). */
function yyyyMmLocal(val) {
  if (val == null) return null;
  if (typeof val === "string" && /^\d{4}-\d{2}/.test(val.trim())) return val.trim().slice(0, 7);
  try {
    const d = val instanceof Date ? val : new Date(String(val));
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  } catch {
    return null;
  }
}

/** Valor faturado efetivo: API pode expor só `valor` (Prisma) quando `valorFaturado` veio null. */
function valorFaturadoDeFaturamento(f) {
  return parseMoneyNumber(f.valor_faturado ?? f.valorFaturado ?? f.valor ?? 0);
}

/** Mês de referência YYYY-MM a partir do registro de faturamento (API + fallbacks). */
function mesReferenciaFaturamento(f) {
  const mr = f.mes_referencia ?? f.mesReferencia;
  if (mr && typeof mr === "string" && /^\d{4}-\d{2}/.test(mr.trim())) return mr.trim().slice(0, 7);
  const dr = f.data_ref ?? f.dataRef;
  const fromRef = yyyyMmLocal(dr);
  if (fromRef) return fromRef;
  const dfat = f.data_faturamento ?? f.dataFaturamento;
  return yyyyMmLocal(dfat);
}

function normalizarIdContrato(id) {
  return id == null ? "" : String(id).trim().toLowerCase();
}

/** Limita quantidade de meses no intervalo (evita intervalos enormes). */
function clampMeses(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 12;
  return Math.min(60, Math.max(1, Math.floor(x)));
}

/**
 * Chaves: futuro-N (a partir do mês atual), passado-N (até o mês atual).
 * Aceita legado "12" como futuro-12.
 */
function parseChavePeriodo(chave) {
  const s = String(chave ?? "").trim();
  const fut = s.match(/^futuro-(\d+)$/i);
  if (fut) return { modo: "futuro", n: clampMeses(fut[1]) };
  const pas = s.match(/^passado-(\d+)$/i);
  if (pas) return { modo: "passado", n: clampMeses(pas[1]) };
  const legacy = parseInt(s, 10);
  if (Number.isFinite(legacy) && legacy > 0) return { modo: "futuro", n: clampMeses(legacy) };
  return { modo: "futuro", n: 12 };
}

export default function ForecastContratosPage() {
  const [periodoMeses, setPeriodoMeses] = useState("futuro-12");
  const [filtroOrgao, setFiltroOrgao] = useState("todos");

  const { data: contratos = [], isLoading: loadingContratos, isError, error } = useQuery({
    queryKey: ["contratos"],
    queryFn: () => entities.Contrato.list(),
  });

  const { data: faturamentos = [], isLoading: loadingFaturamentos } = useQuery({
    queryKey: ["faturamentos", "forecast"],
    queryFn: () => entities.Faturamento.list(),
  });

  const isLoading = loadingContratos || loadingFaturamentos;

  /** Contratos com vigência definida (exclui cancelados). Não exige status "Ativo" — alinhado ao uso real do cadastro. */
  const contratosComVigencia = useMemo(() => {
    return contratos.filter((c) => {
      const s = String(c.status ?? "").trim().toLowerCase();
      if (s === "cancelado" || s === "cancelada") return false;
      const di = c.data_inicio ?? c.dataInicio;
      const df = c.data_fim ?? c.dataFim;
      return !!(di && df);
    });
  }, [contratos]);

  const orgaosUnicos = useMemo(() => {
    const orgaos = new Set(
      contratosComVigencia
        .map((c) => c.orgao_contratante ?? c.orgaoContratante ?? "")
        .filter(Boolean)
    );
    return Array.from(orgaos).sort((a, b) => String(a).localeCompare(String(b)));
  }, [contratosComVigencia]);

  const contratosFiltrados = useMemo(() => {
    if (filtroOrgao === "todos") return contratosComVigencia;
    const orgao = (c) => String(c.orgao_contratante ?? c.orgaoContratante ?? "").trim();
    return contratosComVigencia.filter((c) => orgao(c) === filtroOrgao);
  }, [contratosComVigencia, filtroOrgao]);

  /** Soma valor faturado por YYYY-MM, só dos contratos considerados no filtro */
  const realizadoPorMesRef = useMemo(() => {
    const ids = new Set(contratosFiltrados.map((c) => normalizarIdContrato(c.id)));
    const map = new Map();
    for (const f of faturamentos) {
      const cid = normalizarIdContrato(f.contrato_id ?? f.contratoId);
      if (!cid || !ids.has(cid)) continue;
      const key = mesReferenciaFaturamento(f);
      if (!key) continue;
      const vf = valorFaturadoDeFaturamento(f);
      map.set(key, (map.get(key) ?? 0) + vf);
    }
    return map;
  }, [faturamentos, contratosFiltrados]);

  const dadosForecast = useMemo(() => {
    const hoje = new Date();
    const { modo, n } = parseChavePeriodo(periodoMeses);

    /** Intervalo fechado com exatamente N meses — gráficos e Detalhamento Mensal idênticos à seleção. */
    let dataInicio;
    let dataFim;
    if (modo === "passado") {
      dataInicio = startOfMonth(addMonths(hoje, -(n - 1)));
      dataFim = endOfMonth(hoje);
    } else {
      dataInicio = startOfMonth(hoje);
      dataFim = endOfMonth(addMonths(hoje, n - 1));
    }

    const meses = eachMonthOfInterval({ start: dataInicio, end: dataFim });

    return meses.map((mes) => {
      const mesStr = format(mes, "MMM/yy", { locale: ptBR });
      const mesKey = format(mes, "yyyy-MM");
      let valorPrevisto = 0;

      const mesAtual = new Date(mes.getFullYear(), mes.getMonth(), 1);

      contratosFiltrados.forEach((contrato) => {
        const di = contrato.data_inicio ?? contrato.dataInicio;
        const df = contrato.data_fim ?? contrato.dataFim;
        if (!di || !df) return;
        const dataInicioContrato = safeParseISO(di);
        const dataFimContrato = safeParseISO(df);
        if (!dataInicioContrato || !dataFimContrato) return;

        const contratoMesInicio = new Date(
          dataInicioContrato.getFullYear(),
          dataInicioContrato.getMonth(),
          1
        );
        const contratoMesFim = new Date(dataFimContrato.getFullYear(), dataFimContrato.getMonth(), 1);

        if (mesAtual >= contratoMesInicio && mesAtual <= contratoMesFim) {
          const valorMensal = parseMoneyNumber(
            contrato.valor_mensal_previsto ?? contrato.valorMensalPrevisto
          );
          const valorTotal = parseMoneyNumber(contrato.valor_total ?? contrato.valor);
          if (valorMensal > 0) {
            valorPrevisto += valorMensal;
          } else if (valorTotal > 0) {
            const numMeses = countCalendarMonthsInclusive(dataInicioContrato, dataFimContrato);
            valorPrevisto += valorTotal / numMeses;
          }
        }
      });

      const valorRealizado = realizadoPorMesRef.get(mesKey) ?? 0;

      return {
        mes: mesStr,
        previsto: Math.round(valorPrevisto),
        realizado: Math.round(valorRealizado),
        diferenca: Math.round(valorPrevisto - valorRealizado),
      };
    });
  }, [contratosFiltrados, periodoMeses, realizadoPorMesRef]);

  /**
   * Acumulado real de todos os registros de faturamento dos contratos filtrados
   * (independe do intervalo do gráfico — "Próximos N meses" não inclui meses passados na série).
   */
  const acumuladoFaturamentosFiltrados = useMemo(() => {
    const ids = new Set(contratosFiltrados.map((c) => normalizarIdContrato(c.id)));
    let totalFaturado = 0;
    let totalGlosado = 0;
    for (const f of faturamentos) {
      const cid = normalizarIdContrato(f.contrato_id ?? f.contratoId);
      if (!cid || !ids.has(cid)) continue;
      totalFaturado += valorFaturadoDeFaturamento(f);
      totalGlosado += parseMoneyNumber(f.valor_glosado ?? f.valorGlosado);
    }
    return { totalFaturado, totalGlosado };
  }, [faturamentos, contratosFiltrados]);

  const totais = useMemo(() => {
    const totalPrevistoPeriodo = dadosForecast.reduce((sum, d) => sum + d.previsto, 0);
    const totalRealizadoPeriodo = dadosForecast.reduce((sum, d) => sum + d.realizado, 0);
    const totalAFaturarPeriodo = totalPrevistoPeriodo - totalRealizadoPeriodo;

    return {
      totalPrevisto: totalPrevistoPeriodo,
      totalRealizadoPeriodo,
      /** Soma de todos os faturamentos dos contratos filtrados (todas as competências). */
      totalFaturadoAcumulado: acumuladoFaturamentosFiltrados.totalFaturado,
      totalGlosadoAcumulado: acumuladoFaturamentosFiltrados.totalGlosado,
      totalAFaturar: totalAFaturarPeriodo,
    };
  }, [dadosForecast, acumuladoFaturamentosFiltrados]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando forecast...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <Link to={createPageUrl("Contratos")}>
            <Button variant="ghost" className="mb-4 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <Card className="border-red-200 bg-red-50/80">
            <CardContent className="p-6">
              <p className="text-red-800 font-medium">
                Não foi possível carregar os contratos. Verifique sua conexão e tente novamente.
              </p>
              {error?.message && (
                <p className="text-sm text-red-600 mt-2">{error.message}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const nenhumContrato = contratos.length === 0;
  const nenhumComVigencia = !nenhumContrato && contratosComVigencia.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to={createPageUrl("Contratos")}>
              <Button variant="ghost" className="mb-4 -ml-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-blue-600" />
              Forecast Financeiro
            </h1>
            <p className="text-slate-600 mt-1">Projeção de receitas contratuais</p>
          </div>
        </div>

        {/* Avisos quando não há dados */}
        {nenhumContrato && (
          <Card className="border-amber-200 bg-amber-50/80">
            <CardContent className="p-6">
              <p className="text-amber-800 font-medium">
                Nenhum contrato cadastrado. Cadastre contratos na tela de Contratos para ver a projeção de receitas.
              </p>
              <Link to={createPageUrl("Contratos")}>
                <Button variant="outline" className="mt-3">
                  Ir para Contratos
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
        {nenhumComVigencia && (
          <Card className="border-amber-200 bg-amber-50/80">
            <CardContent className="p-6">
              <p className="text-amber-800 font-medium">
                Nenhum contrato com data de início e fim de vigência preenchidas. Informe as datas nos contratos para ver a projeção (status &quot;Ativo&quot; não é obrigatório).
              </p>
              <Link to={createPageUrl("Contratos")}>
                <Button variant="outline" className="mt-3">
                  Ir para Contratos
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Filtros */}
        <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Período (meses)
                </label>
                <Select value={periodoMeses} onValueChange={setPeriodoMeses}>
                  <SelectTrigger>
                    <SelectValue placeholder="Escolha o período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel className="text-xs text-slate-500">
                        Próximos (a partir do mês atual)
                      </SelectLabel>
                      <SelectItem value="futuro-6">Próximos 6 meses</SelectItem>
                      <SelectItem value="futuro-12">Próximos 12 meses</SelectItem>
                      <SelectItem value="futuro-18">Próximos 18 meses</SelectItem>
                      <SelectItem value="futuro-24">Próximos 24 meses</SelectItem>
                    </SelectGroup>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel className="text-xs text-slate-500">
                        Últimos (períodos passados)
                      </SelectLabel>
                      <SelectItem value="passado-6">Últimos 6 meses</SelectItem>
                      <SelectItem value="passado-12">Últimos 12 meses</SelectItem>
                      <SelectItem value="passado-18">Últimos 18 meses</SelectItem>
                      <SelectItem value="passado-24">Últimos 24 meses</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1.5">
                  Gráficos e detalhamento mensal usam somente os meses deste intervalo.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Órgão/Cliente
                </label>
                <Select value={filtroOrgao} onValueChange={setFiltroOrgao}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os Órgãos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Órgãos</SelectItem>
                    {orgaosUnicos.map((orgao) => (
                      <SelectItem key={String(orgao)} value={String(orgao)}>
                        {String(orgao)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <div className="text-sm text-slate-600">
                  <p className="font-medium">Contratos na projeção: {contratosFiltrados.length}</p>
                  {!nenhumContrato && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Total de contratos: {contratos.length}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Totais */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Previsto</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(totais.totalPrevisto)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Soma no período do gráfico</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Faturado</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {formatCurrency(totais.totalFaturadoAcumulado)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Acumulado (todos os meses dos contratos filtrados)
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">A Faturar</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">
                    {formatCurrency(totais.totalAFaturar)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Previsto − faturado no período do gráfico
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Valor Glosado</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {formatCurrency(totais.totalGlosadoAcumulado)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Soma nos registros de faturamento</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gráfico de Linha */}
        <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Projeção Mensal - Previsto vs Realizado</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={dadosForecast}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("pt-BR", {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(value)
                  }
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: "#000" }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="previsto"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Previsto"
                />
                <Line
                  type="monotone"
                  dataKey="realizado"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Realizado"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de Barras */}
        <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Comparativo Mensal</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dadosForecast}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("pt-BR", {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(value)
                  }
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: "#000" }}
                />
                <Legend />
                <Bar dataKey="previsto" fill="#3b82f6" name="Previsto" />
                <Bar dataKey="realizado" fill="#10b981" name="Realizado" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tabela Detalhada */}
        <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Detalhamento Mensal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">
                      Mês
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">
                      Previsto
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">
                      Realizado
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">
                      Diferença
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">
                      % Realização
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dadosForecast.map((item, index) => {
                    const percentual =
                      item.previsto > 0 ? (item.realizado / item.previsto) * 100 : 0;
                    return (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                          {item.mes}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">
                          {formatCurrency(item.previsto)}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-green-600 font-medium">
                          {formatCurrency(item.realizado)}
                        </td>
                        <td
                          className={`py-3 px-4 text-sm text-right font-medium ${
                            item.diferenca > 0 ? "text-orange-600" : "text-green-600"
                          }`}
                        >
                          {formatCurrency(item.diferenca)}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-slate-600">
                          {percentual.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}